/**
 * taskAutocompleteLabel.ts
 * Pure helper for formatting a TaskSearchResult into an autocomplete display string.
 *
 * Follow-up 1.7 — used both in renderOption and in tests.
 */

import type { TaskSearchResult } from '../services/scheduleTasks';

/**
 * Returns a human-readable label for a task search result.
 * Format: "Task Name (TaskType) — Quote Q-2024-001 | 2024-06-01 → 2024-07-15"
 * When fields are missing, gracefully omits that segment.
 */
export function formatTaskAutocompleteLabel(task: TaskSearchResult): string {
  const parts: string[] = [task.Name];

  if (task.TaskTypeName) {
    parts[0] = `${task.Name} (${task.TaskTypeName})`;
  }

  const sourceParts: string[] = [];

  if (task.SourceType === 'Quote' && task.QuoteNumber) {
    sourceParts.push(`Quote ${task.QuoteNumber}`);
  } else if (task.SourceType === 'WorkOrder' && task.WorkOrderId) {
    sourceParts.push(`WO ${task.WorkOrderId}`);
  } else if (task.SourceType !== 'Manual') {
    sourceParts.push(task.SourceType);
  }

  if (task.StartDate || task.EndDate) {
    const start = task.StartDate ?? '?';
    const end = task.EndDate ?? '?';
    sourceParts.push(`${start} → ${end}`);
  }

  if (sourceParts.length > 0) {
    parts.push(sourceParts.join(' | '));
  }

  return parts.join(' — ');
}

/**
 * Returns a short source identifier chip label.
 * Examples: "Q-2024-001", "WO-2024-042", "Manual"
 */
export function taskSourceChipLabel(task: TaskSearchResult): string {
  if (task.SourceType === 'Quote' && task.QuoteNumber) return task.QuoteNumber;
  if (task.SourceType === 'WorkOrder' && task.WorkOrderId) return task.WorkOrderId;
  return task.SourceType;
}
